/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import filterRefinementParameters from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery$parameters';
import type {
	FilterRefinementTimelineQuery,
	JiraJqlScopeInput,
} from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery.graphql.ts';
import type {
	JiraIssueSearchScope,
	JiraTimelineViewSettings,
	JiraTimelineStaticViewInput,
	JiraIssueSearchSettings,
	JiraIssueSearchStaticViewInput,
	JiraIssueSearchAggregationConfigInput,
	JiraTimelineViewRangeMode,
	JiraTimelineQuery,
} from '@jira/timeline__timeline-view/src/__generated__/JiraTimelineQuery.graphql';
import timelineParameters from '@jira/timeline__timeline-view/src/__generated__/JiraTimelineQuery$parameters';
import { nativeIssueTableEntryPoint } from '@jira/issue-table__issue-table/src/ui/nativeIssueTableEntryPoint.entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import softwareTimelineParameters from './__generated__/JiraSoftwareTimelineAppQuery$parameters.ts';
import type { JiraSoftwareTimelineAppQuery } from './__generated__/JiraSoftwareTimelineAppQuery.graphql.ts';
import type { PreloadedEntryPoints } from './softwareTimelineEntrypoint.entrypoint.types.tsx';

const NAMESPACE = 'TIMELINE';
const TOGGLEABLE_FIELD_SET_IDS = ['assignee', 'status'];

/**
 * The window used to filter sprints shown on the timeline: from one year before
 * the current date to two years after it. Bounds are ISO date-time strings
 * matching the `JiraDateTimeWindow` input.
 */
const getSprintsActiveWithin = (): { start: string; end: string } => {
	const now = new Date();

	const start = new Date(now);
	start.setFullYear(start.getFullYear() - 1);

	const end = new Date(now);
	end.setFullYear(end.getFullYear() + 2);

	return { start: start.toISOString(), end: end.toISOString() };
};

const WEEKS = 'WEEKS' as const;
const MONTHS = 'MONTHS' as const;
const QUARTERS = 'QUARTERS' as const;

type AggregationConfig = {
	aggregationConfig: JiraIssueSearchAggregationConfigInput;
};

type Queries = {
	softwareTimelineQuery: JiraSoftwareTimelineAppQuery;
	timelineQuery: JiraTimelineQuery;
	filterRefinementQuery: FilterRefinementTimelineQuery;
};

type ExtraProps = {
	today: number;
	isTimelineHarmonisationM2Enabled: boolean;
	isTimelineSavedViewEnabled: boolean;
	isTimelineGroupByEnabled: boolean;
	isBulkOperationsInTimelineEnabled: boolean;
	jqlParam: string;
	remountKey?: string;
	filterRefinementScope?: JiraJqlScopeInput;
	includeBoardFilters?: boolean;
	scrollElementHeightCalibration?: { scrollElementHeight: number; viewportHeightOverhead: number };
};

// Matches only plain objects: arrays, `null`, `Date`, `RegExp`, functions, and
// primitives are all excluded, so the `Record<string, unknown>` narrowing
// reflects the exact set of runtime values that reach the property access below.
const isRecord = (value: unknown): value is Record<string, unknown> =>
	Object.prototype.toString.call(value) === '[object Object]';

/**
 * Safely resolves the scroll element height calibration for a route from an
 * untrusted experiment value. Returns `undefined` (so the prop is omitted and
 * the Issue Table uses its built-in fallback) unless the value is a plain object
 * keyed by route name whose entry has finite numeric `scrollElementHeight` and
 * `viewportHeightOverhead`.
 */
const getScrollElementHeightCalibration = (
	calibration: unknown,
	routeName: string,
): { scrollElementHeight: number; viewportHeightOverhead: number } | undefined => {
	if (!isRecord(calibration)) {
		return undefined;
	}

	const entry = calibration[routeName];
	if (!isRecord(entry)) {
		return undefined;
	}

	const { scrollElementHeight, viewportHeightOverhead } = entry;
	if (
		typeof scrollElementHeight !== 'number' ||
		!Number.isFinite(scrollElementHeight) ||
		typeof viewportHeightOverhead !== 'number' ||
		!Number.isFinite(viewportHeightOverhead)
	) {
		return undefined;
	}

	return { scrollElementHeight, viewportHeightOverhead };
};

const and = (clauses: string[]): string => {
	return clauses
		.map((clause) => clause.trim())
		.filter((clause) => clause.length > 0)
		.join(' AND ');
};

const getProjectJql = (projectKey: string) =>
	projectKey.length > 0 ? `project = '${projectKey}'` : '';

const withSpecialValues = (value: string = '') => {
	if (value.indexOf('unassigned') > -1) {
		return 'empty';
	}
	return value;
};

const queryParamToJqlClause = ({ key, queryParam }: { key: string; queryParam: string }) => {
	const values = queryParam.split(',');
	return values.length > 1
		? `${key} in (${values.map(withSpecialValues).join(', ')})`
		: `${key} = ${withSpecialValues(values[0])}`;
};

const getTextSearchJql = (text?: string) => {
	if (!text || text.trim() === '') {
		return '';
	}
	const sanitize = (value: string) =>
		value.trim().replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\t/g, ' ');

	const japaneseAndChineseRegex =
		/[\p{Script_Extensions=Hiragana}\p{Script_Extensions=Katakana}\p{Script_Extensions=Han}]/u;

	let search = sanitize(text);

	// if the text does not contain Chinese or Japanese characters (which are not tokenized),
	// and does not end with a space character
	// make it a wildcard search
	if (!japaneseAndChineseRegex.test(text) && !text.endsWith(' ')) {
		search = `${search}*`;
	}

	return `textfields ~ "${search}"`;
};

function isValidTimelineMode(value: string): value is JiraTimelineViewRangeMode {
	return value === WEEKS || value === MONTHS || value === QUARTERS;
}

const buildJql = ({
	jqlParam = '',
	text,
	projectKey,
	filterQueryParams,
}: {
	jqlParam: string;
	text?: string;
	projectKey: string;
	filterQueryParams: { [key: string]: string };
}): { jql: string; jqlParam: string } => {
	const filterJqlClauses = Object.keys(filterQueryParams).map((key: string) =>
		queryParamToJqlClause({ key, queryParam: filterQueryParams[key] }),
	);
	const project = getProjectJql(projectKey);
	const textSearch = getTextSearchJql(text);
	return {
		jql: `${and([jqlParam, textSearch, ...filterJqlClauses, project])} ORDER BY rank ASC`,
		jqlParam: and([jqlParam, textSearch, ...filterJqlClauses]),
	};
};

// should be equivalent to the getToday function in jira/src/packages/software/roadmap-utils/src/utils/dates.tsx
const getToday = (): number => {
	const now = new Date();
	const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
	return date.valueOf();
};

const parseQueryParamValue = (str?: string): boolean | undefined => {
	try {
		return str ? JSON.parse(str) : undefined;
	} catch {
		// Do nothing - ignore invalid values
	}
};

const parseHierarchyLevelParamValue = (str?: string): number | undefined => {
	if (!str) {
		return undefined;
	}
	const parsed = Number(str);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const toStaticViewInput = (
	settings: JiraIssueSearchSettings,
	aggregationConfig?: AggregationConfig,
): JiraIssueSearchStaticViewInput => {
	return {
		isGroupingEnabled: !!settings.groupBy,
		isHideDoneEnabled: settings.hideDone,
		isHierarchyEnabled: settings.hierarchyEnabled,
		...(aggregationConfig || { aggregationConfig }),
	};
};

type SoftwareTimelineEntrypointComponent = EntryPointComponent<
	Queries,
	{},
	PreloadedEntryPoints,
	ExtraProps
>;

export const softwareTimelineEntrypoint: EntryPoint<
	SoftwareTimelineEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<SoftwareTimelineEntrypointComponent>(
		/* webpackChunkName: "async-harmonised-software-timeline-entrypoint" */ './JiraSoftwareTimelineApp.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		const {
			context,
			tenantContext: { cloudId },
		} = entryPointParams;
		const projectKey = context.match.params.projectKey;
		const boardId = context.match.params.boardId;
		const viewId = context.match.params.viewId;
		const isCMP = context.route.name === 'software-timeline-classic';
		const {
			hideDone,
			jql: jqlQueryParam,
			assignee,
			label,
			statuses,
			issueParent,
			version,
			text,
			goal,
			rangeMode,
			hideWarnings,
			hideDependencies,
			hideVersionHeader,
			hideUnParentedItems,
			maxHierarchyLevel,
			customFilter,
			quickFilter,
		} = context.query;

		if (projectKey == null) {
			throw new Error('Project key missing');
		}

		const prevFieldSetIds = ['issuetype', 'issuekey', 'summary', 'timeline'];
		const { fieldSetIds, fieldSetsInput } = expValEquals(
			'timeline_harmonisation_m2',
			'isEnabled',
			true,
			false,
		)
			? {
					fieldSetIds: undefined,
					fieldSetsInput: {
						viewInput: {
							namespace: 'TIMELINE',
							viewId: 'timeline_default',
						},
					},
				}
			: {
					fieldSetIds: prevFieldSetIds,
					fieldSetsInput: {
						fieldSetIds: prevFieldSetIds,
					},
				};

		// Explicity set the aggregation config to false when the timeline_harmonisation_m2 experiment is disabled.
		const aggregationConfig = !expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
			? {
					aggregationConfig: {
						aggregationFields: [
							{
								fieldId: 'timeline',
								isEnabled: false,
							},
							{
								fieldId: 'status',
								isEnabled: false,
							},
						],
					},
				}
			: undefined;

		const scope: JiraIssueSearchScope | undefined = {
			operationScope: 'TIMELINE_BOARD',
			projectKey,
		};

		// `viewScope` is the same value as `scope` above — it only exists so we can pass the
		// scope to the top-level jiraIssueSearchView behind jira_timeline_hierarchy_board_filter
		// without affecting the nested issues(scope), which must keep `scope` unconditionally.
		// On FG cleanup: delete `viewScope`, pass `scope` directly to jiraIssueSearchView.
		const viewScope: JiraIssueSearchScope | undefined = fg('jira_timeline_hierarchy_board_filter')
			? scope
			: undefined;

		const { jql, jqlParam } = buildJql({
			jqlParam: jqlQueryParam,
			text,
			projectKey,
			filterQueryParams: {
				...(assignee ? { assignee } : {}),
				...(label ? { labels: label } : {}),
				...(statuses ? { statusCategory: statuses } : {}),
				...(issueParent ? { parent: issueParent } : {}),
				...(version ? { fixVersion: version } : {}),
				...(goal ? { '"Goals[Goals]"': goal } : {}),
			},
		});

		const settings: JiraIssueSearchSettings = {
			groupBy: null,
			hideDone: parseQueryParamValue(hideDone),
			hierarchyEnabled: true,
			jql: null,
		};

		// JiraIssueSearchSettings will replace JiraIssueSearchStaticViewInput
		// as part of the saved views project, so we'll use it as the source of
		// truth
		const staticViewInput: JiraIssueSearchStaticViewInput = toStaticViewInput(
			settings,
			aggregationConfig,
		);

		const sourceARI = JiraSoftwareBoardAri.create({
			siteId: cloudId,
			boardId: String(boardId),
		}).toString();

		const filterRefinementScope: JiraJqlScopeInput = {
			backlog: {
				boardId: Number(boardId),
			},
		};

		const normalizedRangeMode = rangeMode?.toUpperCase();
		const validatedRangeMode: JiraTimelineViewRangeMode | undefined =
			normalizedRangeMode && isValidTimelineMode(normalizedRangeMode)
				? normalizedRangeMode
				: undefined;

		const parsedHideDone = parseQueryParamValue(hideDone);
		const parsedHideWarnings = parseQueryParamValue(hideWarnings);
		const parsedHideDependencies = parseQueryParamValue(hideDependencies);
		const parsedHideReleases = parseQueryParamValue(hideVersionHeader);
		const parsedHideUnParentedItems = parseQueryParamValue(hideUnParentedItems);
		const parsedMaxHierarchyLevel = parseHierarchyLevelParamValue(maxHierarchyLevel);
		const timelineToggleOverrides = fg('jira_sw_timeline_saved_view_technical')
			? {
					...(parsedHideWarnings !== undefined ? { hideWarnings: parsedHideWarnings } : {}),
					...(parsedHideDependencies !== undefined
						? { hideDependencies: parsedHideDependencies }
						: {}),
					...(parsedHideReleases !== undefined ? { hideReleases: parsedHideReleases } : {}),
					...(parsedHideUnParentedItems !== undefined
						? { hideUnParentedItems: parsedHideUnParentedItems }
						: {}),
					...(parsedMaxHierarchyLevel !== undefined
						? { maxHierarchyLevel: parsedMaxHierarchyLevel }
						: {}),
				}
			: {};

		const customFilterIds = customFilter ? customFilter.split(',').filter(Boolean) : undefined;
		const quickFilterIds = quickFilter ? quickFilter.split(',').filter(Boolean) : undefined;
		// appliedFilters lives on JiraTimelineViewSettings only (not JiraTimelineStaticViewInput);
		// it's needed so isTimelineViewConfigModified detects a filter override from the URL. The
		// actual issue filtering is applied via issueSearchInput below.
		const appliedFiltersOverride =
			(customFilterIds || quickFilterIds || jqlParam) && fg('jira_sw_timeline_saved_view_technical')
				? {
						appliedFilters: {
							// Use the same `jql` sent to issueSearchInput.customInput.additionalJql below,
							// so the persisted appliedFilters.additionalJql matches the applied search.
							...(jqlParam ? { additionalJql: jqlParam } : {}),
							...(customFilterIds ? { customFilterIds } : {}),
							...(quickFilterIds ? { quickFilterIds } : {}),
						},
					}
				: {};

		const timelineViewSettings: JiraTimelineViewSettings = {
			...(validatedRangeMode !== undefined ? { rangeMode: validatedRangeMode } : {}),
			// hideDone exists on JiraTimelineViewSettings (not JiraTimelineStaticViewInput); it's
			// needed so isTimelineViewConfigModified detects a hideDone override from the URL.
			...(parsedHideDone !== undefined && fg('jira_sw_timeline_saved_view_technical')
				? { hideDone: parsedHideDone }
				: {}),
			...timelineToggleOverrides,
			...appliedFiltersOverride,
		};

		const timelineStaticViewInput: JiraTimelineStaticViewInput = {
			...(validatedRangeMode !== undefined ? { rangeMode: validatedRangeMode } : {}),
			...timelineToggleOverrides,
		};

		const useSavedFilters = !(customFilter || quickFilter || jqlParam);

		const issueSearchInput = {
			customInput: {
				jiraTimelineInput: {
					boardId: String(boardId),
					...(useSavedFilters === false
						? {
								...(jql && !fg('jira_sw_timeline_saved_view_technical')
									? { additionalJql: jql }
									: {}),
								...(jqlParam && fg('jira_sw_timeline_saved_view_technical')
									? { additionalJql: jqlParam }
									: {}),

								...(customFilterIds ? { customFilterIds } : {}),
								...(quickFilterIds ? { quickFilterIds } : {}),
							}
						: {}),
					useSavedFilters,
				},
			},
		};

		const itemIdSuffix = viewId ? `/${viewId}` : '';
		const itemId = expValEquals('jira_sw_timeline_saved_views', 'isEnabled', true, false)
			? `timeline${itemIdSuffix}`
			: 'timeline';

		const boardIdAndItemQuery = {
			cloudId,
			itemId,
			boardId: Number(boardId),
		};
		const jiraViewQueryInput = {
			boardIdAndItemQuery,
		};
		const dynamicSearchKey = `software-timeline-${projectKey}-${boardId}${
			viewId && expValEquals('jira_sw_timeline_saved_views', 'isEnabled', true, false)
				? `-${viewId}`
				: ''
		}`;

		// Per-route scroll element height calibration is delivered via the
		// `issue_table_virtualizer_dynamic_estimators` experiment's `calibration`
		// parameter, a `Record<routeName, ScrollElementHeightCalibration>`. The
		// experiment value is untrusted (it comes from remote config), so it is
		// validated before use; an absent or malformed entry yields `undefined` and
		// the calibration is omitted so the Issue Table falls back to its default.
		const scrollElementHeightCalibration = expVal(
			'issue_table_virtualizer_dynamic_estimators',
			'isEnabled',
			false,
		)
			? getScrollElementHeightCalibration(
					expVal<unknown>('issue_table_virtualizer_dynamic_estimators', 'calibration', null),
					context.route.name,
				)
			: undefined;

		return {
			extraProps: {
				...(expValEquals('jira_sw_timeline_saved_views', 'isEnabled', true, false)
					? { remountKey: JSON.stringify(boardIdAndItemQuery) }
					: {}),
				today: getToday(),
				isTimelineHarmonisationM2Enabled: expValEquals(
					'timeline_harmonisation_m2',
					'isEnabled',
					true,
					false,
				),
				isTimelineSavedViewEnabled: expValEquals(
					'jira_sw_timeline_saved_views',
					'isEnabled',
					true,
					false,
				),
				isTimelineGroupByEnabled: false,
				jqlParam,
				isBulkOperationsInTimelineEnabled: expValEquals(
					'bulk_operations_in_timeline',
					'isEnabled',
					true,
					false,
				),
				...(fg('timeline-filter-refinement-technical-changes')
					? { filterRefinementScope, includeBoardFilters: true }
					: {}),
				...(scrollElementHeightCalibration ? { scrollElementHeightCalibration } : {}),
			},
			queries: {
				softwareTimelineQuery: {
					options: {
						fetchPolicy: fg('jira_software_timeline_perf_improvements')
							? ('store-or-network' as const)
							: ('store-and-network' as const),
					},
					parameters: softwareTimelineParameters,
					variables: {
						cloudId,
						projectKey,
						boardId: sourceARI,
						isCMP,
					},
				},
				timelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: timelineParameters,
					variables: {
						boardIds: [sourceARI],
						amountOfColumns: 500,
						cloudId,
						fieldSetIds,
						fieldSetsInput,
						filterId: null,
						first: fg('jira_software_timeline_perf_improvements') ? 30 : 50,
						issueSearchInput,
						namespace: NAMESPACE,
						projectKey,
						settings,
						/*
									This key is used to generate a unique identity for the issue connection record in the Relay
									store when running a new search. This is to prevent concurrency related bugs occurring when
									performing a refetch that returns a new connection of results. Ordinarily this would be
									handled automatically when relevant query variables change (as these are used to form the
									connection key), however we override the default connection filters which allows us to modify
									variables during pagination operations without producing a new connection key.
									 */
						dynamicSearchKey,
						shouldQueryFieldSetsById: false,
						shouldQueryHasChildrenWithCount: true,
						shouldQueryHasParent: true,
						scope,
						viewScope,
						staticViewInput,
						viewConfigInput: { staticViewInput },
						jiraViewQueryInput,
						// jiraViewQueryInputForIssues is temporary and is used to replace jiraViewQueryInput.
						// It will be removed when jira_timeline_saved_view_technical is cleaned up.
						// When cleaning up jira_timeline_saved_view_technical, replace this with jiraViewQueryInput.
						// Keep this consistent with the business project.
						jiraViewQueryInputForIssues: expValEquals(
							'jira_sw_timeline_saved_views',
							'isEnabled',
							true,
							false,
						)
							? jiraViewQueryInput
							: null,
						timelineViewSettings,
						timelineStaticViewInput,
						viewId: 'timeline_default',
						...(expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
							? {
									toggleableFieldSetIds: TOGGLEABLE_FIELD_SET_IDS,
									isInlineEditingEnabled: true,
									isTimelineHarmonisationM2Enabled: true,
								}
							: {
									isTimelineHarmonisationM2Enabled: false,
									isInlineEditingEnabled: false,
								}),
						shouldQuerySprints: true,
						sprintsActiveWithin: getSprintsActiveWithin(),
						shouldQuerySoftwareProjectSettings: true,
						shouldQueryLastAppliedFilters: true,
						shouldQueryReleases: true,
						shouldQueryBarColor: true,
						isTimelineGroupByEnabled: false,
						projectOrBoardInput: {
							boardId: Number(boardId),
						},
					},
				},
				filterRefinementQuery: {
					options: {
						fetchPolicy: fg('jira_software_timeline_perf_improvements')
							? ('store-or-network' as const)
							: ('store-and-network' as const),
					},
					parameters: filterRefinementParameters,
					variables: {
						cloudId,
						filterRefinementScope,
						boardId: sourceARI,
						includeBoard: true,
					},
				},
			},
			...(fg('jira_software_timeline_perf_improvements')
				? {
						entryPoints: {
							nativeIssueTableEntryPoint: {
								entryPoint: nativeIssueTableEntryPoint,
								entryPointParams,
							},
						},
					}
				: {}),
		};
	},
});
